import ValidationErrors from '@/components/validation-errors';
import { new_registration_path, session_path } from '@/rails/routes';
import type { PageProps } from '@/types';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Link } from '@astryxdesign/core/Link';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';
import { VStack } from '@astryxdesign/core/VStack';
import { useForm, usePage } from '@inertiajs/react';
import { ArrowRight, LockKeyhole, Mail } from 'lucide-react';
import type { FormEvent } from 'react';

interface Props extends PageProps {
  errors: string[];
  formData: {
    email?: string;
  };
}

const NewSession = () => {
  const { errors, formData } = usePage<Props>().props;
  const form = useForm({
    email: formData.email ?? '',
    password: '',
  });

  const passwordError = errors.find((error) => /password/i.test(error));
  const passwordStatus = passwordError
    ? { type: 'error' as const, message: passwordError }
    : undefined;
  const bannerErrors = passwordError ? errors.filter((error) => error !== passwordError) : errors;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    form.transform((data) => ({ session: data }));
    form.post(session_path());
  };

  return (
    <main className="iorio-auth-page">
      <VStack as="section" gap={8} width="100%" className="iorio-auth-shell">
        <HStack vAlign="center" width="100%" className="iorio-auth-brand-row">
          <HStack gap={2} vAlign="center">
            <img src="/iorio-icon.png" alt="" className="iorio-auth-mark" />
            <Text type="body" weight="bold" size="lg">
              Iorio
            </Text>
          </HStack>
        </HStack>

        <section className="iorio-auth-grid" aria-label="Inicio de sesión de Iorio">
          <Card padding={8} width="100%" className="iorio-auth-card">
            <form onSubmit={submit}>
              <VStack gap={5} hAlign="stretch">
                <ValidationErrors errors={bannerErrors} />

                <VStack gap={2}>
                  <Heading level={1}>Entrar a Iorio</Heading>
                </VStack>

                <VStack gap={3}>
                  <TextInput
                    label="Email"
                    type="email"
                    htmlName="email"
                    placeholder="nombre@empresa.com"
                    value={form.data.email}
                    onChange={(value) => form.setData('email', value)}
                    size="lg"
                    startIcon={Mail}
                    isRequired
                    hasAutoFocus
                  />

                  <TextInput
                    label="Contraseña"
                    type="password"
                    htmlName="password"
                    placeholder="Tu contraseña"
                    value={form.data.password}
                    onChange={(value) => form.setData('password', value)}
                    size="lg"
                    startIcon={LockKeyhole}
                    isRequired
                    status={passwordStatus}
                  />
                </VStack>

                <Button
                  label="Ingresar"
                  variant="primary"
                  size="lg"
                  type="submit"
                  icon={<ArrowRight size={18} />}
                  isDisabled={form.processing}
                  isLoading={form.processing}
                />

                <Text type="supporting" color="secondary">
                  ¿Todavía no tenés cuenta?{' '}
                  <Link href={new_registration_path()} type="supporting" hasUnderline>
                    Crear cuenta
                  </Link>
                </Text>
              </VStack>
            </form>
          </Card>
        </section>
      </VStack>
    </main>
  );
};

export default NewSession;
